import React from 'react';
import { useTranslation } from 'react-i18next';
import * as format from '../../utils/formatters';
import shortCurrency from '../../utils/shortCurrency';
import { Rightsholder, RightsholderPlacement, Sponsor } from '@app/types/api';
import Page from './Page';
import ExposureCard from './ExposureCard';
import VerbalMentionContextBox from './VerbalMentionContextBox';

const isFiniteMetric = (value?: number) =>
    typeof value === 'number' && Number.isFinite(value);

const formatMetricNumber = (value?: number) => {
    if (!isFiniteMetric(value) || value === 0) {
        return '-';
    }

    return format.decimal(value);
};

const formatMetricImpressions = (value?: number) => {
    if (!isFiniteMetric(value) || value === 0) {
        return '-';
    }

    return format.abbreviatedDecimal(value);
};

const formatMetricCurrency = (value: number | undefined, currencyCode: string) => {
    if (!isFiniteMetric(value) || value === 0) {
        return '-';
    }

    return shortCurrency(value, 1, currencyCode);
};

interface BroadcastExposurePDFProps {
    exposureType: 'visual' | 'verbal_mention';
    broadcastName: string;
    rightsholder: Rightsholder;
    sponsor: Sponsor;
    placement: RightsholderPlacement;
    total: {
        exposures?: number;
        exposuresImpressions?: number;
        duration?: number;
        sponsorMediaValue?: number;
    };
    frameUrls?: (string | null)[];
    verbalMentionsExample?: {
        mention?: string;
        context: string[];
        videoUrl?: string; // This will be the link to the modal
    };
    currencyCode: string;
    networks: string[];
}

function BroadcastExposurePDF({
    exposureType,
    broadcastName,
    frameUrls,
    rightsholder,
    verbalMentionsExample,
    sponsor,
    placement,
    total,
    currencyCode,
    networks,
}: BroadcastExposurePDFProps) {
    const { t } = useTranslation('pdf');

    const containerStyle = {
        maxWidth: '100%',
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column' as const,
        alignItems: 'center',
        justifyContent: 'center',
    };

    const tableStyle = {
        width: '100%',
        borderCollapse: 'collapse' as const,
    };

    const headerCellStyle = {
        fontWeight: '500' as const,
        fontSize: '14px',
        color: '#333',
    };

    const dataCellStyle = {
        textAlign: 'center' as const,
        fontSize: '14px',
        color: '#666',
    };

    const metricsTableStyle = {
        width: '100%',
        borderCollapse: 'collapse' as const,
        border: '1px solid #e0e0e0',
        borderRadius: '4px',
    };

    const metricsHeaderStyle = {
        textAlign: 'center' as const,
        padding: '6px',
        fontWeight: '600' as const,
        fontSize: '14px',
        color: '#333',
        border: '1px solid #e0e0e0',
    };

    const metricsCellStyle = {
        textAlign: 'center' as const,
        padding: '6px',
        fontSize: '14px',
        color: '#666',
        border: '1px solid #e0e0e0',
    };

    const networkHeaderStyle = {
        textAlign: 'center' as const,
        padding: '6px',
        fontSize: '14px',
        fontWeight: '600' as const,
        color: 'black',
        backgroundColor: '#0000000A',
        border: '1px solid #e0e0e0',
    };

    const networkCellStyle = {
        textAlign: 'center' as const,
        padding: '6px',
        fontSize: '14px',
        border: '1px solid #e0e0e0',
        color: 'black',
    };

    return (
        <div style={containerStyle}>
            <Page>
                <ExposureCard
                    broadcastName={broadcastName}
                    frameUrl={frameUrls?.at(0) ?? undefined}
                    verbalMentionsExample={verbalMentionsExample}
                >
                    {exposureType === 'verbal_mention' &&
                        verbalMentionsExample?.context?.map(
                            (context, index) => (
                                <VerbalMentionContextBox
                                    key={`${broadcastName}-${index}`}
                                    context={context}
                                    mention={verbalMentionsExample?.mention}
                                />
                            ),
                        )}

                    <table style={tableStyle}>
                        <thead>
                            <tr style={{ borderBottom: '1px solid #e0e0e0' }}>
                                <th style={headerCellStyle}>
                                    {t('broadcast.rightsholder')}
                                </th>
                                <th style={headerCellStyle}>
                                    {t('broadcast.sponsor')}
                                </th>
                                <th style={headerCellStyle}>
                                    {t('broadcast.placement')}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td style={dataCellStyle}>
                                    {rightsholder.name}
                                </td>
                                <td style={dataCellStyle}>{sponsor.name}</td>
                                <td style={dataCellStyle}>{placement.name}</td>
                            </tr>
                        </tbody>
                    </table>

                    <div>
                        <table style={metricsTableStyle}>
                            <thead>
                                <tr style={{ backgroundColor: '#E6F4FF' }}>
                                    <th style={metricsHeaderStyle}>
                                        {t('broadcast.exposures')}
                                    </th>
                                    <th style={metricsHeaderStyle}>
                                        {t('broadcast.impressions')}
                                    </th>
                                    <th style={metricsHeaderStyle}>
                                        {t('broadcast.duration')}
                                    </th>
                                    <th style={metricsHeaderStyle}>
                                        {t('broadcast.smv')}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td style={metricsCellStyle}>
                                        {formatMetricNumber(total.exposures)}
                                    </td>
                                    <td style={metricsCellStyle}>
                                        {formatMetricImpressions(
                                            total.exposuresImpressions,
                                        )}
                                    </td>
                                    <td style={metricsCellStyle}>
                                        {formatMetricNumber(total.duration)}
                                    </td>
                                    <td style={metricsCellStyle}>
                                        {formatMetricCurrency(
                                            total.sponsorMediaValue,
                                            currencyCode,
                                        )}
                                    </td>
                                </tr>
                                <tr>
                                    <td colSpan={4} style={networkHeaderStyle}>
                                        {t('broadcast.networks')}
                                    </td>
                                </tr>
                                <tr>
                                    <td colSpan={4} style={networkCellStyle}>
                                        {networks.join(' • ')}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </ExposureCard>
            </Page>

            {exposureType === 'visual' &&
                frameUrls &&
                frameUrls.filter((u): u is string => u != null).length > 1 && (
                    <Page>
                        {frameUrls
                            .slice(1)
                            .filter((u): u is string => u != null)
                            .map((frameUrl, index) => (
                                <ExposureCard
                                    key={`${broadcastName}-${index}`}
                                    broadcastName={broadcastName}
                                    frameUrl={frameUrl}
                                />
                            ))}
                    </Page>
                )}
        </div>
    );
}

export default BroadcastExposurePDF;
